/**
 * Bandi e contributi per chi organizza (docs/06-sito-web.md, "Bandi e contributi"): avvisi
 * pubblici (Regione, ministeri, fondazioni…) raccolti dalle sorgenti `bandi`, letti con l'AI e
 * filtrati per chi organizza (chi è, cosa vuole fare, dove). Liste chiuse condivise da AI,
 * sito e pannello. Senza zod: si usa anche nel browser (`@sse/schema/bando`); il modello zod è
 * in `bando-model.ts`.
 */
/** Chi può fare domanda (domanda "Chi sei?" del questionario). */
export const BANDO_APPLICANTS = [
    'proloco',
    'associazione',
    'comitato',
    'comune',
    'impresa',
];
export const BANDO_APPLICANT_LABELS = {
    proloco: 'Pro Loco',
    associazione: 'Associazione (APS, ETS)',
    comitato: 'Comitato feste o parrocchia',
    comune: 'Comune',
    impresa: 'Impresa o organizzatore privato',
};
/** Cosa si finanzia (domanda "Cosa vuoi fare?"). */
export const BANDO_ACTIVITIES = [
    'sagra',
    'festa',
    'spettacolo',
    'festival',
    'rievocazione',
    'natale',
    'sport',
    'promozione',
    'attrezzature',
];
export const BANDO_ACTIVITY_LABELS = {
    sagra: 'Sagra o evento enogastronomico',
    festa: 'Festa patronale o riti del fuoco',
    spettacolo: 'Concerti, spettacoli, bande',
    festival: 'Festival o rassegna culturale',
    rievocazione: 'Rievocazione storica',
    natale: 'Eventi di Natale',
    sport: 'Evento sportivo',
    promozione: 'Promozione turistica del territorio',
    attrezzature: 'Attrezzature o investimenti',
};
/** Attività che sono eventi: con una di queste la domanda è per un evento. */
export const EVENT_ACTIVITIES = [
    'sagra',
    'festa',
    'spettacolo',
    'festival',
    'rievocazione',
    'natale',
    'sport',
];
/**
 * Che domanda chiede il bando, e quindi che dati servono per "Chiedi a noi la domanda":
 * - `evento`: un evento (sagra, festa, concerto…), con date, edizione e pubblico;
 * - `impresa`: un'impresa che nasce o investe (es. avvisi dei GAL SRE04-SRD14 per le start-up),
 *   con la sede, il settore e gli investimenti;
 * - `progetto`: un progetto o un'attività che non è un singolo evento (residenze artistiche,
 *   promozione del territorio, attrezzature).
 */
export const APPLICATION_FORMS = ['evento', 'impresa', 'progetto'];
export const APPLICATION_FORM_LABELS = {
    evento: 'Un evento',
    impresa: "Un'impresa (avvio o investimenti)",
    progetto: 'Un progetto o attività',
};
/**
 * Il tipo di domanda del bando: quello scritto sul bando (dall'AI o dalla redazione) o, se
 * manca, dedotto da chi e cosa: solo imprese → `impresa`; un'attività da evento (o tutte) →
 * `evento`; altrimenti `progetto`.
 */
export function applicationForm(b) {
    if (b.form)
        return b.form;
    if (b.applicants.length && b.applicants.every((a) => a === 'impresa'))
        return 'impresa';
    if (!b.activities.length ||
        b.activities.some((a) => EVENT_ACTIVITIES.includes(a)))
        return 'evento';
    return 'progetto';
}
/**
 * Linea ricorrente fuori dall'edizione in corso: "in arrivo", con i mesi tipici di uscita. Con
 * un'edizione aperta (`deadline` non ancora passata) la linea vale come un bando aperto fino alla
 * scadenza, poi torna in attesa della prossima.
 */
export function awaitingEdition(b, today) {
    return Boolean(b.recurring) && !(b.deadline && b.deadline >= today);
}
/** Com'è l'impresa che chiede il contributo (domanda di tipo `impresa`). */
export const BUSINESS_STAGES = ['nuova', 'attiva'];
export const BUSINESS_STAGE_LABELS = {
    nuova: 'Da avviare (impresa nuova)',
    attiva: 'Già attiva, con una nuova attività',
};
/**
 * Cosa offre: `fondi` (contributi in denaro, il caso normale) o `volontari` (persone, per esempio
 * il Servizio Civile tramite UNPLI: niente importo né domanda per un evento). Senza `kind`:
 * fondi.
 */
export const BANDO_KINDS = ['fondi', 'volontari'];
/** Fasce di importo massimo per progetto (domanda facoltativa "Quanto ti serve?"). */
export const BANDO_AMOUNTS = ['lt5k', '5k_20k', '20k_100k', 'gt100k'];
export const BANDO_AMOUNT_LABELS = {
    lt5k: 'Fino a 5.000 €',
    '5k_20k': '5.000-20.000 €',
    '20k_100k': '20.000-100.000 €',
    gt100k: 'Oltre 100.000 €',
};
/** Fascia di un importo massimo in euro. */
export const amountBand = (euro) => euro <= 5000 ? 'lt5k' : euro <= 20000 ? '5k_20k' : euro <= 100000 ? '20k_100k' : 'gt100k';
/** Territorio: tutta Italia o UE, la Puglia, alcune province, alcuni comuni. */
export const BANDO_SCOPES = ['ue', 'italia', 'puglia', 'province', 'comuni'];
/** Stati: `pending` da rivedere, `published` sul sito, `rejected` scartato. */
export const BANDO_STATUSES = ['pending', 'published', 'rejected'];
export const MONTH_LABELS = [
    'gennaio',
    'febbraio',
    'marzo',
    'aprile',
    'maggio',
    'giugno',
    'luglio',
    'agosto',
    'settembre',
    'ottobre',
    'novembre',
    'dicembre',
];
const MIN_AMOUNT = {
    lt5k: 0,
    '5k_20k': 5000,
    '20k_100k': 20000,
    gt100k: 100000,
};
/** Vale per il comune (codice ISTAT, provincia con la sigla)? Senza comune: sì. */
export function coversComune(b, comune) {
    if (!comune)
        return true;
    if (b.area.scope === 'comuni')
        return (b.area.comuneIds ?? []).includes(comune.istat);
    if (b.area.scope === 'province')
        return (b.area.provinces ?? []).includes(comune.province);
    return true;
}
/**
 * Il bando va bene per i filtri? Chi e cosa filtrano, il comune tiene i bandi che valgono lì,
 * l'importo toglie quelli con un massimo più basso. Regole semplici, senza AI: si capisce perché
 * un bando c'è. Stato e scadenza si guardano a parte.
 */
export function bandoFits(b, f, comune) {
    if (f.who && !b.applicants.includes(f.who))
        return false;
    if (f.what.length &&
        b.activities.length &&
        !b.activities.some((a) => f.what.includes(a)))
        return false;
    if (!coversComune(b, comune))
        return false;
    if (f.amount && b.maxAmount !== undefined && b.maxAmount < MIN_AMOUNT[f.amount])
        return false;
    return true;
}
/** I filtri come query della pagina dei bandi (`?who=proloco&what=sagra,festa…`). */
export function bandoFiltersQuery(f) {
    const q = [];
    if (f.who)
        q.push(['who', f.who]);
    if (f.what.length)
        q.push(['what', f.what.join(',')]);
    if (f.comune)
        q.push(['comune', f.comune]);
    if (f.amount)
        q.push(['amount', f.amount]);
    // come URLSearchParams (la virgola diventa %2C), senza dipendere dal browser o da Node
    return q.length ? `?${q.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&')}` : '';
}
const DESCRIPTION_MIN = 20;
const DESCRIPTION_GOOD = 300;
const daysBetween = (from, to) => Math.round((Date.parse(`${to}T12:00:00Z`) - Date.parse(`${from}T12:00:00Z`)) / 86_400_000);
/** Il contributo più alto che si può chiedere: importo massimo e quota coperta sul budget. */
export function maxRequest(b, budget) {
    const byCoverage = b.coveragePercent !== undefined && budget !== undefined
        ? Math.floor((budget * b.coveragePercent) / 100)
        : undefined;
    const limits = [b.maxAmount, byCoverage, budget].filter((x) => x !== undefined);
    return limits.length ? Math.min(...limits) : undefined;
}
/**
 * Controlli del form in funzione del bando (stessi nel browser e nell'API): `errors` blocca il
 * controllo con l'AI e l'invio; `hints` sono suggerimenti per aumentare le possibilità. I campi
 * obbligatori dipendono dal tipo di domanda (`applicationForm`): le date solo per un evento, lo
 * stato dell'impresa solo per un'impresa. Per un'impresa la quota coperta non blocca: negli
 * avvisi a più interventi (premio di avviamento al 100% e investimenti al 50%) la valuta l'AI.
 */
export function validateApplication(b, a, ctx) {
    const form = applicationForm(b);
    const errors = [];
    const hints = [];
    const need = (field, ok) => {
        if (!ok)
            errors.push({ field, code: 'required' });
        return ok;
    };
    // obbligatori
    if (need('applicant', Boolean(a.applicant)) && !b.applicants.includes(a.applicant))
        errors.push({
            field: 'applicant',
            code: 'applicant_not_allowed',
            params: { allowed: b.applicants.join(',') },
        });
    if (form === 'impresa')
        need('stage', Boolean(a.stage));
    else if (need('activity', Boolean(a.activity)) &&
        b.activities.length &&
        !b.activities.includes(a.activity))
        errors.push({
            field: 'activity',
            code: 'activity_not_allowed',
            params: { allowed: b.activities.join(',') },
        });
    need('event', Boolean(a.event?.trim()));
    if (need('comuneId', Boolean(a.comuneId)) && ctx.comune && !coversComune(b, ctx.comune))
        errors.push({ field: 'comuneId', code: 'comune_outside' });
    // le date servono per un evento; per un'impresa o un progetto sono facoltative
    if (form === 'evento' ? need('dateFrom', Boolean(a.dateFrom)) : Boolean(a.dateFrom)) {
        if (a.dateFrom < ctx.today)
            errors.push({ field: 'dateFrom', code: 'date_past' });
        if (a.dateTo && a.dateTo < a.dateFrom)
            errors.push({ field: 'dateTo', code: 'dates_order' });
    }
    if (need('description', Boolean(a.description?.trim())) &&
        a.description.trim().length < DESCRIPTION_MIN)
        errors.push({ field: 'description', code: 'too_short', params: { min: DESCRIPTION_MIN } });
    const hasBudget = need('budget', a.budget !== undefined && a.budget > 0);
    if (need('requested', a.requested !== undefined && a.requested > 0) && hasBudget) {
        const r = a.requested;
        if (r > a.budget)
            errors.push({ field: 'requested', code: 'requested_over_budget' });
        else if (b.maxAmount !== undefined && r > b.maxAmount)
            errors.push({ field: 'requested', code: 'requested_over_max', params: { max: b.maxAmount } });
        else if (form !== 'impresa' &&
            b.coveragePercent !== undefined &&
            r > (a.budget * b.coveragePercent) / 100)
            errors.push({
                field: 'requested',
                code: 'requested_over_coverage',
                params: { max: Math.floor((a.budget * b.coveragePercent) / 100), pct: b.coveragePercent },
            });
    }
    // il bando (una linea ricorrente scaduta aspetta la prossima edizione: si prepara in anticipo)
    const waiting = awaitingEdition(b, ctx.today);
    if (b.deadline && b.deadline < ctx.today && !waiting)
        errors.push({ field: 'bando', code: 'bando_closed' });
    // requisiti (passo 1)
    for (const c of b.checks)
        if (ctx.answers[c.id] === false)
            errors.push({ field: 'checks', code: 'check_no', params: { question: c.question } });
    const unanswered = b.checks.filter((c) => ctx.answers[c.id] === undefined).length;
    if (unanswered)
        errors.push({ field: 'checks', code: 'checks_missing', params: { n: unanswered } });
    // suggerimenti per aumentare le possibilità
    const top = form === 'impresa' ? undefined : maxRequest(b, a.budget);
    if (top !== undefined && a.requested !== undefined && a.requested < top * 0.9)
        hints.push({ field: 'requested', code: 'request_room', params: { max: top } });
    if ((a.description?.trim().length ?? 0) < DESCRIPTION_GOOD)
        hints.push({
            field: 'description',
            code: 'description_short',
            params: { min: DESCRIPTION_GOOD },
        });
    if (form === 'evento' && !a.edition?.trim())
        hints.push({ field: 'edition', code: 'add_edition' });
    if (form === 'impresa' && !a.sector?.trim())
        hints.push({ field: 'sector', code: 'add_sector' });
    if (!a.audience?.trim())
        hints.push({ field: 'audience', code: 'add_audience' });
    if ((a.needs?.trim().length ?? 0) < 40)
        hints.push({ field: 'needs', code: 'needs_detail' });
    const ready = ctx.done.filter((d) => b.documents.includes(d)).length;
    if (b.documents.length && ready < b.documents.length)
        hints.push({
            field: 'bando',
            code: 'documents_pending',
            params: { n: ready, of: b.documents.length },
        });
    if (b.deadline && b.deadline >= ctx.today) {
        const left = daysBetween(ctx.today, b.deadline);
        if (left <= 15)
            hints.push({ field: 'bando', code: 'deadline_close', params: { days: left } });
    }
    if ((b.opensAt && b.opensAt > ctx.today) || waiting)
        hints.push({
            field: 'bando',
            code: 'not_open_yet',
            params: b.opensAt && b.opensAt > ctx.today ? { date: b.opensAt } : {},
        });
    return { errors, hints };
}
//# sourceMappingURL=bando.js.map